import {useState} from "react"
import {Button, Spinner} from "@heroui/react"
import Badge from "@/components/general/ui/Badge"
import {AlertType, AlertActivationStatus} from "../alertTypes"
import ChannelIndicator from "@/components/archive/ChannelIndicator"
import {
  IconDotsVertical,
  IconCircleDashed,
  IconCircleDashedCheck,
  IconChevronDown,
  IconChevronUp,
  IconMessage,
  IconUsers,
} from "@tabler/icons-react"

import {AlertItem} from "./AlertItem"
import {IconContainer} from "./IconContainer"

interface GroupedAlertsListProps {
  unresolvedAlerts: AlertType[]
  resolvedAlerts: AlertType[]
  loading?: boolean
  updatingItems?: Set<string>
  optimisticUpdateFunctions?: {
    setItemUpdating?: (alertId: string, isUpdating: boolean) => void
    updateAlertOptimistically?: (
      alertId: string,
      updates: Partial<AlertType>
    ) => void
    moveAlertBetweenLists?: (
      alertId: string,
      fromStatus: AlertActivationStatus,
      toStatus: AlertActivationStatus,
      updates?: Partial<AlertType>
    ) => void
    refetchInBackground?: () => Promise<void>
  }
}

interface ThreadGroup {
  threadUid: string
  alerts: AlertType[]
  unresolvedAlerts: AlertType[]
  resolvedAlerts: AlertType[]
  latestAlert: AlertType
  alertCount: number
}

// Component for individual thread groups
const ThreadGroupItem: React.FC<{
  group: ThreadGroup
  updatingItems?: Set<string>
  optimisticUpdateFunctions?: GroupedAlertsListProps["optimisticUpdateFunctions"]
}> = ({group, updatingItems, optimisticUpdateFunctions}) => {
  const [expanded, setExpanded] = useState(false)
  const {latestAlert, alerts, unresolvedAlerts, resolvedAlerts, alertCount} =
    group

  const isUpdating = group.alerts.some((alert) => updatingItems?.has(alert.id))

  // Format participants list similar to archive view
  const formatParticipants = (participants: string[] | undefined) => {
    if (!participants || participants.length === 0) return "No participants"
    if (participants.length === 2) {
      return participants.join(" & ")
    }
    return participants.join(", ")
  }

  return (
    <div
      className={`group relative transition-colors hover:bg-gray-50 ${isUpdating ? "opacity-70" : ""}`}
    >
      {isUpdating && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/50 backdrop-blur-sm">
          <Spinner size="sm" color="primary" />
        </div>
      )}

      {/* Thread Group Header */}
      <div
        className="flex cursor-pointer items-center justify-between px-4 py-3"
        onClick={() => !isUpdating && setExpanded(!expanded)}
      >
        <div className="min-w-0 flex-1">
          {/* Subject line - exact archive styling */}
          {latestAlert.firstMessageSubject && (
            <div className="mb-2 flex flex-row items-center gap-1">
              <ChannelIndicator
                channel_type="email"
                size="xs"
                containerClass="px-1 -ml-1"
              />
              <p className="text-sm font-medium text-blue-500">
                {latestAlert.firstMessageSubject}
              </p>
            </div>
          )}

          {/* Thread indicator with alert count */}
          <div className="mb-2 flex items-center gap-2">
            <div className="flex items-center gap-1">
              <IconContainer>
                <IconMessage className="h-4 w-4 text-blue-500" />
              </IconContainer>
              <span className="text-sm font-medium text-gray-700">
                Email thread
              </span>
            </div>

            {/* Badges show the full story */}
            {unresolvedAlerts.length > 0 && (
              <Badge variant="red">{unresolvedAlerts.length} unresolved</Badge>
            )}
            {resolvedAlerts.length > 0 && unresolvedAlerts.length === 0 && (
              <Badge variant="green">{resolvedAlerts.length} resolved</Badge>
            )}

            {/* Sensitivity badge if any alerts are sensitive */}
            {alerts.some(
              (alert) =>
                alert.maxSensitivity !== null && alert.maxSensitivity > 0
            ) && <Badge variant="purple">Sensitive</Badge>}
          </div>

          {/* Participants */}
          <p className="text-sm text-gray-600">
            {formatParticipants(latestAlert.participants)}
          </p>
        </div>

        {/* Right side - timestamp and expand button */}
        <div className="flex items-center gap-3 text-right">
          <p className="whitespace-nowrap text-sm text-gray-500">
            {new Date(latestAlert.timestamp).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </p>
          <Button
            isIconOnly
            variant="light"
            size="sm"
            className="text-gray-400 hover:text-gray-600"
            onPress={() => !isUpdating && setExpanded(!expanded)}
          >
            {expanded ? (
              <IconChevronUp size={16} />
            ) : (
              <IconChevronDown size={16} />
            )}
          </Button>
        </div>
      </div>

      {/* Expanded Content - Individual Alerts */}
      {expanded && (
        <div className="border-t border-gray-100 bg-gray-50">
          <div className="pl-6">
            {" "}
            {/* Indent the individual alerts */}
            {alerts.map((alert) => (
              <AlertItem
                key={alert.id}
                alert={alert}
                isUpdating={updatingItems?.has(alert.id)}
                optimisticUpdateFunctions={optimisticUpdateFunctions}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export const GroupedAlertsList: React.FC<GroupedAlertsListProps> = ({
  unresolvedAlerts,
  resolvedAlerts,
  loading = false,
  updatingItems,
  optimisticUpdateFunctions,
}) => {
  const [unresolvedOpen, setUnresolvedOpen] = useState(true)
  const [resolvedOpen, setResolvedOpen] = useState(false)

  // Show loading state
  if (loading) {
    return (
      <div className="mt-2 rounded-xl border border-neutral-200 bg-white p-8 text-center shadow-sm">
        <Spinner size="lg" color="primary" />
        <p className="mt-4 text-sm text-neutral-600">Loading alerts...</p>
      </div>
    )
  }

  // Group alerts by threadUid
  const groupAlertsByThread = (alerts: AlertType[]) => {
    const threadMap = new Map<string, AlertType[]>()
    const ungroupedAlerts: AlertType[] = []

    alerts.forEach((alert) => {
      if (alert.threadUid) {
        if (!threadMap.has(alert.threadUid)) {
          threadMap.set(alert.threadUid, [])
        }
        threadMap.get(alert.threadUid)!.push(alert)
      } else {
        ungroupedAlerts.push(alert)
      }
    })

    // Convert to ThreadGroup objects
    const threadGroups: ThreadGroup[] = Array.from(threadMap.entries()).map(
      ([threadUid, alerts]) => {
        // Sort alerts by timestamp (newest first)
        const sortedAlerts = alerts.sort(
          (a, b) =>
            new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
        )

        const unresolvedInGroup = sortedAlerts.filter(
          (alert) => alert.status !== "resolved" && alert.status !== "dismissed"
        )
        const resolvedInGroup = sortedAlerts.filter(
          (alert) => alert.status === "resolved" || alert.status === "dismissed"
        )

        return {
          threadUid,
          alerts: sortedAlerts,
          unresolvedAlerts: unresolvedInGroup,
          resolvedAlerts: resolvedInGroup,
          latestAlert: sortedAlerts[0],
          alertCount: alerts.length,
        }
      }
    )

    // Sort thread groups by latest alert timestamp (newest first)
    threadGroups.sort(
      (a, b) =>
        new Date(b.latestAlert.timestamp).getTime() -
        new Date(a.latestAlert.timestamp).getTime()
    )

    return {threadGroups, ungroupedAlerts}
  }

  const allAlerts = [...unresolvedAlerts, ...resolvedAlerts]
  const {threadGroups, ungroupedAlerts} = groupAlertsByThread(allAlerts)

  // Separate thread groups into resolved/unresolved based on their content
  const unresolvedThreadGroups = threadGroups.filter(
    (group) => group.unresolvedAlerts.length > 0
  )
  const resolvedThreadGroups = threadGroups.filter(
    (group) =>
      group.unresolvedAlerts.length === 0 && group.resolvedAlerts.length > 0
  )

  // Separate ungrouped alerts
  const ungroupedUnresolved = ungroupedAlerts.filter(
    (alert) => alert.status !== "resolved" && alert.status !== "dismissed"
  )
  const ungroupedResolved = ungroupedAlerts.filter(
    (alert) => alert.status === "resolved" || alert.status === "dismissed"
  )

  const totalUnresolved =
    unresolvedThreadGroups.length + ungroupedUnresolved.length
  const totalResolved = resolvedThreadGroups.length + ungroupedResolved.length

  return (
    <div className="mt-2 rounded-xl border border-neutral-200 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-neutral-100 px-5 py-3">
        <h3 className="text-base font-medium text-neutral-800">
          Alert Activity (Grouped by Thread)
        </h3>
        <div className="flex items-center gap-2">
          <button className="rounded-full p-1 hover:bg-neutral-100">
            <IconDotsVertical className="h-4 w-4 text-neutral-500" />
          </button>
        </div>
      </div>

      <div className="rounded-b-xl">
        {/* Unresolved Section */}
        <div className="border-b border-neutral-100">
          <button
            onClick={() => setUnresolvedOpen(!unresolvedOpen)}
            className="flex w-full items-center justify-between bg-neutral-100 px-4 py-3 text-sm font-medium text-neutral-700 hover:bg-neutral-200"
          >
            <div className="flex items-center gap-1">
              <div className="-ml-0.5 flex h-4 w-4 items-center justify-center">
                <IconCircleDashed className="h-4 w-4 text-red-500" />
              </div>
              <span>Unresolved Issues</span>
              {totalUnresolved > 0 && (
                <Badge variant="red">{totalUnresolved}</Badge>
              )}
            </div>
            {unresolvedOpen ? (
              <IconChevronUp className="h-4 w-4 text-neutral-400" />
            ) : (
              <IconChevronDown className="h-4 w-4 text-neutral-400" />
            )}
          </button>

          {unresolvedOpen && (
            <div className="divide-y-1 divide-gray-300">
              {/* Thread groups with unresolved alerts */}
              {unresolvedThreadGroups.map((group) => (
                <ThreadGroupItem
                  key={group.threadUid}
                  group={group}
                  updatingItems={updatingItems}
                  optimisticUpdateFunctions={optimisticUpdateFunctions}
                />
              ))}

              {/* Individual ungrouped unresolved alerts */}
              {ungroupedUnresolved.map((alert) => (
                <AlertItem
                  key={alert.id}
                  alert={alert}
                  isUpdating={updatingItems?.has(alert.id)}
                  optimisticUpdateFunctions={optimisticUpdateFunctions}
                />
              ))}

              {totalUnresolved === 0 && (
                <div className="py-6 text-center text-sm text-neutral-500">
                  No unresolved alerts
                </div>
              )}
            </div>
          )}
        </div>

        {/* Resolved Section */}
        <div className="border-t-2 border-gray-300">
          <button
            onClick={() => setResolvedOpen(!resolvedOpen)}
            className="flex w-full items-center justify-between bg-neutral-100 px-4 py-3 text-sm font-medium text-neutral-700 hover:bg-neutral-200"
          >
            <div className="flex items-center gap-1">
              <div className="-ml-0.5 flex h-4 w-4 items-center justify-center">
                <IconCircleDashedCheck className="h-4 w-4 text-green-500" />
              </div>
              <span>Resolved Issues</span>
              {totalResolved > 0 && (
                <Badge variant="green">{totalResolved}</Badge>
              )}
            </div>
            {resolvedOpen ? (
              <IconChevronUp className="h-4 w-4 text-neutral-400" />
            ) : (
              <IconChevronDown className="h-4 w-4 text-neutral-400" />
            )}
          </button>

          {resolvedOpen && (
            <div className="divide-y-1 divide-gray-300">
              {/* Thread groups with only resolved alerts */}
              {resolvedThreadGroups.map((group) => (
                <ThreadGroupItem
                  key={group.threadUid}
                  group={group}
                  updatingItems={updatingItems}
                  optimisticUpdateFunctions={optimisticUpdateFunctions}
                />
              ))}

              {/* Individual ungrouped resolved alerts */}
              {ungroupedResolved.map((alert) => (
                <AlertItem
                  key={alert.id}
                  alert={alert}
                  isUpdating={updatingItems?.has(alert.id)}
                  optimisticUpdateFunctions={optimisticUpdateFunctions}
                />
              ))}

              {totalResolved === 0 && (
                <div className="py-6 text-center text-sm text-neutral-500">
                  No resolved alerts
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
